"use client";

import { useSearchFocus } from "@/app/context";
import { useOnClickOutside } from "@/app/hooks/useOnClickOutside";
import type { FlightPlaces } from "@/types/flights";
import { isEmpty } from "lodash";
import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { DestinationFieldDropdown } from "./DestinationFieldDropdown";

type Props = {
  value: string;
  label: string;
  options: FlightPlaces[];
  onChange: (value: string) => void;
  onClick: (flight: FlightPlaces) => void;
  otherValue: string;
  isMultiCity?: boolean;
  onClear: () => void;
};

export const DestinationDropdown = ({
  value,
  label,
  options,
  onChange,
  onClick,
  isMultiCity,
  onClear,
}: Props) => {
  const t = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);
  const { registerInput } = useSearchFocus();

  const [isOpen, setOpen] = useState(false);
  const ref = useOnClickOutside<HTMLDivElement>(() => setOpen(false));

  const handleClick = ({ name, iataCode }: FlightPlaces) => {
    setOpen((prev) => !prev);
    onClick({ name, iataCode });
  };

  useEffect(() => {
    registerInput(label, inputRef);
  }, [label, registerInput]);

  return (
    <div className="flex-grow relative" ref={ref}>
      <label
        className="flex gap-x-2 items-center rounded-full border border-sleap-gray-400 sm:border-none h-12 dark:border-input"
        onClick={() => setOpen(true)}
      >
        <span
          className={`font-semibold font-switzer text-base text-sleap-gray-950 dark:text-white text-nowrap ${label == "To" ? "sm:ps-0 ps-6" : "ps-6"}`}
        >
          {label}
        </span>
        <input
          type="text"
          ref={inputRef}
          className={`h-12 outline-none w-full rounded-full font-switzer text-base bg-transparent font-medium ${label == "To" ? "pe-6" : isMultiCity ? "pe-2" : "pe-0"}`}
          value={value}
          placeholder={`${label == t("HomePageFlights.to") ? t("HomePageFlights.destinationPlaceholder") : t("HomePageFlights.departurePlaceholder")}`}
          onChange={(e) => onChange(e.target.value)}
        />
        {!isEmpty(value) && (
          <button
            className="p-0 mr-6 flex items-center justify-center w-6 h-6 shrink-0"
            type="button"
            onClick={onClear}
          >
            <X size={16} strokeWidth={3} />
          </button>
        )}
      </label>
      {isOpen && (
        <DestinationFieldDropdown options={options} onClick={handleClick} />
      )}
    </div>
  );
};
